/**
 * Integration API Service for OAuth flows and data connectors
 */

import { z } from 'zod';

import { apiService } from './api';

/**
 * Providers this firm has already connected, as `CloudProvider` values
 * ("box", "dropbox", "google_drive") — the same predicate the backend freezes
 * connect on.
 *
 * Deliberately NOT the `source_registry` list: that also ticks demo snapshots
 * and seeded systems nobody ever connected, so hiding Connect off it would hide
 * the button on tenants that can still connect and show it on tenants that
 * would only get a 409.
 */
const connectedProvidersSchema = z.object({
  connected_providers: z.array(z.string()),
  reauthorization_required_providers: z.array(z.string()).default([]),
});

export interface ConnectionStatus {
  connectedProviders: string[];
  reauthorizationRequiredProviders: string[];
}

export const listConnectedProviders = async (): Promise<ConnectionStatus> => {
  const raw = await apiService.get<unknown>('/api/v1/integrations/connections');
  const parsed = connectedProvidersSchema.parse(raw);
  return {
    connectedProviders: parsed.connected_providers,
    reauthorizationRequiredProviders: parsed.reauthorization_required_providers,
  };
};

// Box Integration Types
export interface BoxAuthResponse {
  auth_url: string;
}

export interface BoxAuthenticateRequest {
  auth_code: string;
  /**
   * The nonce the backend minted when it built the authorize URL, echoed back
   * by the provider. Not optional: the backend refuses a callback it cannot tie
   * to a flow this user started, which is what stops a logged-in user being
   * walked into completing an attacker's authorization with their own session.
   */
  state: string;
}

/**
 * The connect confirmation. Deliberately carries no provider tokens: the Box
 * grant is persisted server-side against the firm's `cloud_accounts` row and
 * never crosses into the browser, so there is nothing here for an XSS to steal
 * and nothing for a caller to substitute.
 */
export interface BoxAuthenticateResponse {
  connected: boolean;
  provider: string;
  user_info: {
    id: string | null;
    name: string | null;
    email: string | null;
  };
}

// Box OAuth Functions
export const getBoxAuthUrl = async (): Promise<BoxAuthResponse> => {
  return apiService.get<BoxAuthResponse>('/api/v1/integrations/box/auth-url');
};

export const authenticateBox = async (
  authCode: string,
  state: string
): Promise<BoxAuthenticateResponse> => {
  return apiService.post<BoxAuthenticateResponse>(
    '/api/v1/integrations/box/authenticate',
    { auth_code: authCode, state }
  );
};

// Dropbox OAuth Functions
//
// The same shape as Box's, on purpose: connecting Dropbox is the same
// once-per-firm admin event, gated by the same backend freeze, storing the
// grant in the same table. What differs is what happens next — Box's callback
// triggers an in-app sync, Dropbox's does not, because its sync is a scheduled
// job outside the app (`connectors/dropbox_to_gcs` + the registration bridge).
export const getDropboxAuthUrl = async (): Promise<BoxAuthResponse> => {
  return apiService.get<BoxAuthResponse>('/api/v1/integrations/dropbox/auth-url');
};

export const authenticateDropbox = async (
  authCode: string,
  state: string
): Promise<BoxAuthenticateResponse> => {
  return apiService.post<BoxAuthenticateResponse>(
    '/api/v1/integrations/dropbox/authenticate',
    { auth_code: authCode, state }
  );
};

export const getSharePointAuthUrl = async (): Promise<BoxAuthResponse> => {
  return apiService.get<BoxAuthResponse>('/api/v1/integrations/sharepoint/auth-url');
};

export const authenticateSharePoint = async (
  authCode: string,
  state: string
): Promise<BoxAuthenticateResponse> => {
  return apiService.post<BoxAuthenticateResponse>(
    '/api/v1/integrations/sharepoint/authenticate',
    { auth_code: authCode, state }
  );
};

/**
 * Which flow a `state` nonce was minted for, read off its prefix.
 *
 * Two flows share Box's one redirect URI (connecting the firm, and exporting a
 * folder), so the callback page has to know which endpoint to post to before it
 * can post anything — and it cannot read an opaque token. The prefix is that
 * routing hint and **nothing more**: the backend hashes the whole state string
 * and takes the purpose from its own stored row, so rewriting the prefix only
 * sends the request to an endpoint whose purpose check then rejects it.
 *
 * Returns `null` for an absent or unrecognised state, and the caller must treat
 * that as a refusal — there is no legitimate callback without one.
 */
export type OAuthStatePurpose = 'connect' | 'export';

export const oauthStatePurpose = (
  state: string | null | undefined
): OAuthStatePurpose | null => {
  if (!state) return null;
  const prefix = state.split('.')[0];
  return prefix === 'connect' || prefix === 'export' ? prefix : null;
};

// Helper function to extract the `state` nonce the provider echoed back.
export const extractOAuthStateFromUrl = (url: string): string | null => {
  try {
    return new URLSearchParams(url.split('?')[1]).get('state');
  } catch {
    return null;
  }
};

// Helper function to extract auth code from URL
export const extractAuthCodeFromUrl = (
  url: string,
  provider: 'box' | 'dropbox' | 'sharepoint'
): string | null => {
  try {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    return urlParams.get('code');
  } catch (error) {
    console.error(`Failed to extract auth code for ${provider}:`, error);
    return null;
  }
};

export interface BoxSyncResponse {
  downloaded_files: Array<{
    id: string;
    filename: string;
    original_filename: string;
    file_size: number;
    mime_type: string;
    gcs_url: string;
    downloaded_at: string;
  }>;
  total_downloaded: number;
  errors: Array<{
    file_id: string;
    error: string;
  }>;
  sync_type: string;
  status: string;
}

// Sync the firm's connected Box account. No payload: the backend resolves the
// stored CloudAccount for the caller's client and uses its credentials.
export const syncBoxAccount = async (): Promise<BoxSyncResponse> => {
  return apiService.post<BoxSyncResponse>('/api/v1/integrations/box/sync', {});
};

/**
 * Purge provider tokens this app used to keep in localStorage.
 *
 * Kept as a one-way cleanup, not as storage: browsers that ran an older build
 * still hold a live Box access/refresh pair, and nothing else would ever remove
 * it. Delete this once every session has been through a build containing it.
 */
export const purgeLegacyProviderTokens = () => {
  for (const provider of ['box', 'google-drive']) {
    localStorage.removeItem(`${provider}_access_token`);
    localStorage.removeItem(`${provider}_refresh_token`);
  }
};

/*
 * NO HIERARCHY CLIENT LIVES HERE, DELIBERATELY.
 *
 * `GET /integrations/hierarchy/{provider}` (and its `/folder` and `/search`
 * siblings, and the `cloud_files` signed-url route the tree's file rows used)
 * existed to render connector-synced folder trees in the deal-room sidebar.
 * Connector data never renders there (epic locked decision 4): it reaches the
 * user through agent answers over the catalog. The browser therefore has no
 * reason to enumerate a firm's connector storage, and this module offers no way
 * to ask.
 *
 * Re-adding a fetcher here is what would let a catalog tile fire a per-provider
 * request again — the shape of the guaranteed-404 Google Drive request this
 * epic removed. `config/dataSources.test.tsx` asserts these exports are absent;
 * `components/Sidebar.dealRoom.test.tsx` asserts the sidebar issues no such
 * request even if one were reachable by another route.
 */
